import { LoadableResponse } from "api-types";
import { collection, getDocs, query } from "firebase/firestore/lite";

import { useMemo, useState } from "react";

import { useFirebase } from "../firebase/useFirebase";

export type MetadataCollections =
  | "allergens"
  | "preferences"
  | "cuisines"
  | "allergen-mappings"
  | "disclaimers";

/**
 * Query an internal metadata collection (like allergens / preferences)
 *
 * This data doesn't change, and hence we don't listen for updates.
 */
export function useFirestoreMetadata<T>(
  language: string,
  metadataCollection: MetadataCollections
): LoadableResponse<T[]> {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<T[]>([]);
  const firebase = useFirebase();

  useMemo(() => {
    const docRefs = query(
      collection(
        firebase.firestore,
        `metadata-language/${language}/${metadataCollection}`
      )
    );
    getDocs(docRefs)
      .then((querySnapshot) => {
        setData(querySnapshot.docs.map((doc) => doc.data() as T));
        setLoading(false);
      })
      .catch((error) => {
        console.error(
          `Error getting metadata ${metadataCollection} / ${language}`,
          error
        );
        setLoading(false);
      });
  }, [firebase, language, metadataCollection]);

  return {
    isLoading: loading,
    data: data,
  };
}
